import fp from "lodash/fp";
import {
	getFormAsyncErrors,
	getFormSyncErrors,
	getFormValues,
	touch,
} from "redux-form";

import { getFullName } from "helpers";
import {
	asyncValidationService,
	dropdownValuesService,
	merchantGeneralTabService,
	merchantListService,
} from "services";

import { generalConstants } from "./general.constants";
import { mapMerchantFormToModel } from "./mapping";

export const merchantGeneralActions = {
	addMerchant,
	addMerchantLogo,
	checkIsDiscoverMidUnique,
	deleteMerchantLogo,
	editGeneralInfo,
	getHierarchy,
	getMerchant,
	isMidUnique,
	loadReferralPartners,
	loadSalesAgents,
	setBanks,
	setBillingBanks,
	setDiscoverMidValidationPending,
	setHighRisk,
	setMidValidationPending,
	setOriginalMidValidationPending,
	setPreviewLogo,
	validateGeneralFormAndGetNextStep,
	validateOriginalMid,
	validateTicketsForm,
};

// private functions

const getAllFormErrors = (formName, state, dispatch) => {
	const fields = state.form[formName].registeredFields;

	dispatch(touch(formName, ...Object.keys(fields)));

	const syncErrors = getFormSyncErrors(formName)(state) || {};
	const asyncErrors = getFormAsyncErrors(formName)(state) || {};

	return { ...syncErrors, ...asyncErrors };
};

const manualFormAsyncValidation = async ({
	isMidValid,
	isMidBeingValidated,
	isMidValidationPending,

	isOriginalMidValid,
	isOriginalMidBeingValidated,
	isOriginalMidValidationPending,

	isDiscoverMidValid,
	isDiscoverMidBeingValidated,
	isDiscoverMidValidationPending,

	isParent,
	mid,
	originalMID,
	discoverMid,
	merchantId,
}) => {
	// Signer validation
	let midValid = isParent || (isMidValid && !isMidBeingValidated);
	if (!midValid && isMidValidationPending) {
		const midValidResp = await asyncValidationService.isMidUnique(
			mid,
			merchantId,
		);
		midValid = !midValidResp.merchantExists;
	}
	if (!midValid) return;

	// Original MID validation
	let originalMidValid =
		!originalMID || (isOriginalMidValid && !isOriginalMidBeingValidated);
	if (!originalMidValid && isOriginalMidValidationPending) {
		const midValidResp = await asyncValidationService.validateOriginalMid(
			originalMID,
			merchantId,
		);
		originalMidValid = !midValidResp.merchantExists;
	}
	if (!originalMidValid) return;

	// Discover MID validation
	let discoverMidValid =
		!discoverMid || (isDiscoverMidValid && !isDiscoverMidBeingValidated);
	if (!discoverMidValid && isDiscoverMidValidationPending) {
		const midValidResp =
			await asyncValidationService.checkIsDiscoverMidUnique(discoverMid);
		discoverMidValid = !midValidResp.merchantExists;
	}
	if (!discoverMidValid) return;
};

// end private functions

function getMerchant(id) {
	return dispatch => {
		dispatch(request());

		return merchantGeneralTabService.getMerchant(id).then(
			async result => {
				if (result?.routing) {
					const body = {
						pattern: result?.routing,
						page: 0,
						pageSize: 20,
					};
					const banks = await dropdownValuesService.getBanks(body);
					dispatch(merchantGeneralActions.setBanks(banks?.items));
				}
				dispatch(success(result));
				return Promise.resolve(result);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return { type: generalConstants.MERCHANT_GET_GENERAL_DATA_REQUEST };
	}
	function success(generalData) {
		return {
			type: generalConstants.MERCHANT_GET_GENERAL_DATA_SUCCESS,
			generalData,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_GET_GENERAL_DATA_ERROR,
			error,
		};
	}
}

function validateGeneralFormAndGetNextStep(merchantId) {
	const formName = "generalEditForm";

	return async (dispatch, getState) => {
		const state = getState();
		const formValues = getFormValues(formName)(state);
		const formErrors = getAllFormErrors(formName, state, dispatch);

		await manualFormAsyncValidation({
			isMidValid: state.merchantGeneral.isMidValid,
			isMidValidationPending:
				state.merchantGeneral.isMidValidationPending,
			isMidBeingValidated: state.merchantGeneral.loading.isMidValid,

			isDiscoverMidValid: state.merchantGeneral.isDiscoverMidValid,
			isDiscoverMidValidationPending:
				state.merchantGeneral.isDiscoverMidValidationPending,
			isDiscoverMidBeingValidated:
				state.merchantGeneral.loading.isDiscoverMidValid,

			isOriginalMidValid: state.merchantGeneral.isOriginalMidValid,
			isOriginalMidValidationPending:
				state.merchantGeneral.isOriginalMidValidationPending,
			isOriginalMidBeingValidated:
				state.merchantGeneral.loading.isOriginalMidValid,

			isParent: formValues.isParent,
			mid: formValues.mid,
			originalMID: formValues.originalMID,
			discoverMid: formValues.discoverMid,
			merchantId,
		});

		if (!fp.isEmpty(formErrors)) {
			return Promise.reject("Validation failed");
		} else {
			return Promise.resolve();
		}
	};
}

function validateTicketsForm() {
	const formName = "ticketsForm";
	return async (dispatch, getState) => {
		const state = getState();
		const errors = getAllFormErrors(formName, state, dispatch);

		if (!fp.isEmpty(errors)) return Promise.reject("Validation failed");

		return Promise.resolve();
	};
}

function editGeneralInfo() {
	const formName = "generalEditForm";
	return (dispatch, getState) => {
		const state = getState();
		const formValues = getFormValues(formName)(state) || {};
		const model = mapMerchantFormToModel(formValues);

		dispatch(request());

		return merchantGeneralTabService.editGeneralInfo(model).then(
			result => {
				dispatch(success(result));
				return Promise.resolve(result);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return { type: generalConstants.MERCHANT_UPDATE_GENERAL_DATA_REQUEST };
	}
	function success() {
		return { type: generalConstants.MERCHANT_UPDATE_GENERAL_DATA_SUCCESS };
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_UPDATE_GENERAL_DATA_ERROR,
			error,
		};
	}
}

function addMerchant() {
	const generalEditFormName = "generalEditForm";
	const ticketsFormName = "ticketsForm";
	return (dispatch, getState) => {
		const state = getState();
		const ticketsFormValues = getFormValues(ticketsFormName)(state) || {};
		const generalEditFormValues = getFormValues(generalEditFormName)(state);

		const model = mapMerchantFormToModel(
			generalEditFormValues,
			ticketsFormValues,
		);

		dispatch(request());

		return merchantGeneralTabService.addMerchant(model).then(
			result => {
				dispatch(success(result));
				return Promise.resolve(result);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return { type: generalConstants.MERCHANT_ADD_REQUEST };
	}
	function success(model) {
		return { type: generalConstants.MERCHANT_ADD_SUCCESS, model };
	}
	function failure(error) {
		return { type: generalConstants.MERCHANT_ADD_ERROR, error };
	}
}

// mid
function setMidValidationPending() {
	return {
		type: generalConstants.MERCHANT_SET_MID_VALIDATION_PENDING,
	};
}
function isMidUnique(mid, merchantId) {
	return dispatch => {
		dispatch(request());

		return asyncValidationService.isMidUnique(mid, merchantId).then(
			result => {
				dispatch(success(result));
				return Promise.resolve(result);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return { type: generalConstants.MERCHANT_VALIDATE_MID_REQUEST };
	}
	function success({ merchantExists }) {
		return {
			type: generalConstants.MERCHANT_VALIDATE_MID_SUCCESS,
			isMidValid: !merchantExists,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_VALIDATE_MID_ERROR,
			error,
		};
	}
}

// Discover MID
function setDiscoverMidValidationPending() {
	return {
		type: generalConstants.MERCHANT_SET_DISCOVER_MID_VALIDATION_PENDING,
	};
}
function checkIsDiscoverMidUnique(mid) {
	return dispatch => {
		dispatch(request());

		return asyncValidationService.checkIsDiscoverMidUnique(mid).then(
			result => {
				dispatch(success(result));
				return Promise.resolve(result);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return {
			type: generalConstants.MERCHANT_VALIDATE_DISCOVER_MID_REQUEST,
		};
	}
	function success({ merchantExists }) {
		return {
			type: generalConstants.MERCHANT_VALIDATE_DISCOVER_MID_SUCCESS,
			isDiscoverMidValid: !merchantExists,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_VALIDATE_DISCOVER_MID_ERROR,
			error,
		};
	}
}

// Original MID
function setOriginalMidValidationPending() {
	return {
		type: generalConstants.MERCHANT_SET_ORIGINAL_MID_VALIDATION_PENDING,
	};
}
function validateOriginalMid(originalMID, merchantId) {
	return dispatch => {
		dispatch(request());

		return asyncValidationService
			.validateOriginalMid(originalMID, merchantId)
			.then(
				result => {
					dispatch(success(result));
					return Promise.resolve(result);
				},
				error => {
					dispatch(failure(error));
					return Promise.reject(error);
				},
			);
	};

	function request() {
		return {
			type: generalConstants.MERCHANT_VALIDATE_ORIGINAL_MID_REQUEST,
		};
	}
	function success({ merchantExists }) {
		return {
			type: generalConstants.MERCHANT_VALIDATE_ORIGINAL_MID_SUCCESS,
			isOriginalMidValid: !merchantExists,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_VALIDATE_ORIGINAL_MID_ERROR,
			error,
		};
	}
}

function setBanks(banks) {
	return {
		type: generalConstants.MERCHANT_SET_BANKS,
		banks,
	};
}

function setBillingBanks(banks) {
	return {
		type: generalConstants.MERCHANT_SET_BILLING_BANKS,
		billingBanks: banks,
	};
}

function addMerchantLogo(merchantId, document) {
	return dispatch => {
		return merchantGeneralTabService
			.addMerchantLogo(merchantId, document)
			.then(
				result => {
					dispatch(success(result));
					return Promise.resolve();
				},
				error => {
					dispatch(failure(error));
					return Promise.reject();
				},
			);
	};

	function success() {
		return {
			type: generalConstants.MERCHANT_ADD_BRANDING_LOGO_SUCCESS,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_ADD_BRANDING_LOGO_ERROR,
			error,
		};
	}
}

function deleteMerchantLogo(merchantId) {
	return dispatch => {
		return merchantGeneralTabService.deleteMerchantLogo(merchantId).then(
			result => dispatch(success(result)),
			error => dispatch(failure(error)),
		);
	};

	function success() {
		return {
			type: generalConstants.MERCHANT_DELETE_BRANDING_LOGO_SUCCESS,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_DELETE_BRANDING_LOGO_ERROR,
			error,
		};
	}
}

function setPreviewLogo(previewLogo) {
	return {
		type: generalConstants.SET_MERCHANT_LOGO,
		previewLogo,
	};
}

function loadSalesAgents(pattern, prevOptions, { page, formName }) {
	return async (dispatch, getState) => {
		dispatch(request());

		var companyId = getFormValues(formName)(getState())?.company?.id?.value;

		try {
			const { agents, count } = await merchantListService.getAgentList(
				pattern,
				page,
				true,
				companyId,
			);

			const hasMore = prevOptions.length + agents.length < count;
			dispatch(success(agents));

			return {
				options: agents.map(i => ({
					value: { id: i.id },
					label: getFullName(i),
				})),
				hasMore,
				additional: {
					page: page + 1,
					formName,
				},
			};
		} catch (err) {
			dispatch(failure(err));
		}
	};

	function request() {
		return {
			type: generalConstants.LOAD_SALES_AGENTS_REQUEST,
		};
	}

	function success(salesAgents) {
		return {
			type: generalConstants.LOAD_SALES_AGENTS_SUCCESS,
			salesAgents,
		};
	}

	function failure(error) {
		return {
			type: generalConstants.LOAD_SALES_AGENTS_FAILURE,
			error,
		};
	}
}

function loadReferralPartners(pattern, prevOptions, { page }) {
	return async dispatch => {
		dispatch(request());

		try {
			const { agents, count } = await merchantListService.getAgentList(
				pattern,
				page,
				true,
			);

			const hasMore = prevOptions.length + agents.length < count;
			dispatch(success(agents));

			return {
				options: agents.map(i => ({
					value: { id: i.id },
					label: getFullName(i),
				})),
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		} catch (err) {
			dispatch(failure(err));
		}
	};

	function request() {
		return {
			type: generalConstants.LOAD_REFERRAL_PARTNERS_REQUEST,
		};
	}

	function success(referralPartners) {
		return {
			type: generalConstants.LOAD_REFERRAL_PARTNERS_SUCCESS,
			referralPartners,
		};
	}

	function failure(error) {
		return {
			type: generalConstants.LOAD_REFERRAL_PARTNERS_FAILURE,
			error,
		};
	}
}

function getHierarchy({ merchantId, page, pageSize }) {
	return dispatch => {
		dispatch(request());

		return merchantGeneralTabService
			.getHierarchy({ merchantId, page, pageSize })
			.then(
				result => {
					dispatch(success(result));
					return Promise.resolve(result);
				},
				error => {
					dispatch(failure(error));
					return Promise.reject(error);
				},
			);
	};

	function request() {
		return { type: generalConstants.MERCHANT_GET_HIERARCHY_REQUEST };
	}
	function success(hierarchy) {
		return {
			type: generalConstants.MERCHANT_GET_HIERARCHY_SUCCESS,
			hierarchy,
		};
	}
	function failure(error) {
		return {
			type: generalConstants.MERCHANT_GET_HIERARCHY_ERROR,
			error,
		};
	}
}

function setHighRisk(highRiskValue) {
	return {
		type: generalConstants.MERCHANT_SET_HIGH_RISK,
		highRiskValue,
	};
}
